import { useState, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { Link } from "react-router-dom";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Switch } from "@/components/ui/switch";
import { Label } from "@/components/ui/label";

interface CookiePreferences {
  essential: boolean;
  analytics: boolean;
  preference: boolean;
  marketing: boolean;
}

export const CookieBanner = () => {
  const [showBanner, setShowBanner] = useState(false);
  const [showCustomize, setShowCustomize] = useState(false);
  const [preferences, setPreferences] = useState<CookiePreferences>({
    essential: true,
    analytics: false,
    preference: false,
    marketing: false,
  });

  useEffect(() => {
    const consent = localStorage.getItem("cookie-consent");
    if (!consent) {
      setShowBanner(true);
    } else {
      const savedPreferences = localStorage.getItem("cookie-preferences");
      if (savedPreferences) {
        setPreferences(JSON.parse(savedPreferences));
      }
    }
  }, []);

  const handleAcceptAll = () => {
    const allAccepted = {
      essential: true,
      analytics: true,
      preference: true,
      marketing: true,
    };
    localStorage.setItem("cookie-consent", "accepted");
    localStorage.setItem("cookie-preferences", JSON.stringify(allAccepted));
    setPreferences(allAccepted);
    setShowBanner(false);
  };

  const handleDeclineAll = () => {
    const essentialOnly = {
      essential: true,
      analytics: false,
      preference: false,
      marketing: false,
    };
    localStorage.setItem("cookie-consent", "declined");
    localStorage.setItem("cookie-preferences", JSON.stringify(essentialOnly));
    setPreferences(essentialOnly);
    setShowBanner(false);
  };

  const handleSavePreferences = () => {
    localStorage.setItem("cookie-consent", "custom");
    localStorage.setItem("cookie-preferences", JSON.stringify(preferences));
    setShowCustomize(false);
    setShowBanner(false);
  };

  const updatePreference = (key: keyof CookiePreferences, value: boolean) => {
    setPreferences(prev => ({ ...prev, [key]: value }));
  };

  if (!showBanner) return null;

  return (
    <>
      <div className="fixed bottom-0 left-0 right-0 z-50 border-t border-border bg-card p-4 shadow-elevated animate-slide-in">
        <div className="container flex flex-col sm:flex-row items-center justify-between gap-4">
          <div className="flex-1 text-sm text-muted-foreground">
            <p>
              We use cookies to enhance your experience. By continuing to visit this site you agree to our use of cookies.{" "}
              <Link to="/cookies" className="text-primary hover:underline">
                Learn more
              </Link>
            </p>
          </div>
          <div className="flex gap-2 flex-wrap justify-center">
            <Button variant="outline" onClick={handleDeclineAll}>
              Decline All
            </Button>
            <Button variant="outline" onClick={() => setShowCustomize(true)}>
              Customize
            </Button>
            <Button onClick={handleAcceptAll}>Accept All</Button>
          </div>
        </div>
      </div>

      <Dialog open={showCustomize} onOpenChange={setShowCustomize}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>Customize Cookie Preferences</DialogTitle>
            <DialogDescription>
              Choose which types of cookies you want to allow. Essential cookies cannot be disabled as they are required for the site to function.
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-6 py-4">
            <div className="flex items-center justify-between space-x-4">
              <div className="flex-1">
                <Label htmlFor="essential" className="text-base font-semibold">
                  Essential Cookies
                </Label>
                <p className="text-sm text-muted-foreground">
                  Required for the website to function properly. These cannot be disabled.
                </p>
              </div>
              <Switch
                id="essential"
                checked={preferences.essential}
                disabled
              />
            </div>

            <div className="flex items-center justify-between space-x-4">
              <div className="flex-1">
                <Label htmlFor="analytics" className="text-base font-semibold">
                  Analytics Cookies
                </Label>
                <p className="text-sm text-muted-foreground">
                  Help us understand how you use our platform to improve your experience.
                </p>
              </div>
              <Switch
                id="analytics"
                checked={preferences.analytics}
                onCheckedChange={(checked) => updatePreference("analytics", checked)}
              />
            </div>

            <div className="flex items-center justify-between space-x-4">
              <div className="flex-1">
                <Label htmlFor="preference" className="text-base font-semibold">
                  Preference Cookies
                </Label>
                <p className="text-sm text-muted-foreground">
                  Remember your settings and preferences for a personalized experience.
                </p>
              </div>
              <Switch
                id="preference"
                checked={preferences.preference}
                onCheckedChange={(checked) => updatePreference("preference", checked)}
              />
            </div>

            <div className="flex items-center justify-between space-x-4">
              <div className="flex-1">
                <Label htmlFor="marketing" className="text-base font-semibold">
                  Marketing Cookies
                </Label>
                <p className="text-sm text-muted-foreground">
                  Used to deliver more relevant advertising and track campaign effectiveness.
                </p>
              </div>
              <Switch
                id="marketing"
                checked={preferences.marketing}
                onCheckedChange={(checked) => updatePreference("marketing", checked)}
              />
            </div>
          </div>

          <div className="flex gap-2 justify-end">
            <Button variant="outline" onClick={() => setShowCustomize(false)}>
              Cancel
            </Button>
            <Button onClick={handleSavePreferences}>
              Save Preferences
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
};
